import {
  AccordionControl,
  Accordion as MantineAccordion,
  type AccordionControlProps as MantineAccordionControlProps,
  type AccordionItemProps as MantineAccordionItemProps,
  type AccordionProps as MantineAccordionProps,
} from '@mantine/core';
import type { ReactNode } from 'react';
import styled, { css, useTheme } from 'styled-components';
import type { ITheme } from '../../utils/theme';
import ArrowsDown from '../icon/arrows-down';
import Icon, { IconName } from '../icon/icon';
import { IconScale } from '../icon/types';
import { Tooltip } from '../tooltip/tooltip';

export type AccordionProps = MantineAccordionProps<true> & {
  size?: 'sm' | 'md';
};

type AccordionControlProps = MantineAccordionControlProps & {
  sideContent?: ReactNode;
  tooltip?: string;
};

const ICON_SIZE_MAP = {
  sm: IconScale.S,
  md: IconScale.M,
};

const Accordion = ({ size = 'md', ...props }: AccordionProps) => {
  return (
    <StyledAccordion
      multiple
      chevron={<ArrowsDown size={ICON_SIZE_MAP[size]} />}
      chevronSize={24}
      size={size}
      {...props}
    />
  );
};

type AccordionItemProps = MantineAccordionItemProps & {
  theme: ITheme;
  $hasBorder?: boolean;
};

const Item = styled(MantineAccordion.Item)`
  ${({ theme, $hasBorder = true }: AccordionItemProps) => css`
    border-bottom: ${$hasBorder
      ? `1px dashed ${theme.palette.neutral[60]}`
      : '0'};
  `}
`;

const Control = ({
  sideContent,
  tooltip,
  children,
  ...props
}: AccordionControlProps) => {
  const theme = useTheme();
  return (
    <AccordionControl icon={sideContent} {...props}>
      <StyledTooltip tooltipText={tooltip} noAnchorRole>
        {children}
        {tooltip && (
          <InfoIcon
            name={IconName.Info}
            scale={IconScale.XS}
            color={theme.palette.neutral[80]}
          />
        )}
      </StyledTooltip>
    </AccordionControl>
  );
};

export default Object.assign(Accordion, {
  Item,
  Control,
  Panel: MantineAccordion.Panel,
});

const getStyle = (theme: ITheme, size: string) => {
  if (size === 'sm') {
    return {
      spacing: theme.spacing(0.5),
      typography: theme.typography.bodyMd2,
      chevronWidth: theme.spacing(2),
    };
  }
  return {
    spacing: theme.spacing(2),
    typography: theme.typography.bodyLg2,
    chevronWidth: theme.spacing(3),
  };
};

const StyledAccordion = styled(MantineAccordion<true>)`
  ${({ theme, size }: { theme: ITheme; size: string }) => {
    const { spacing, typography, chevronWidth } = getStyle(theme, size);

    return css`
      .mantine-Accordion {
        &-label {
          font-size: ${typography.fontSize};
          font-weight: ${typography.fontWeight};
          padding-top: ${spacing};
          padding-bottom: ${spacing};
        }

        &-icon {
          font-size: ${typography.fontSize};
          font-weight: ${typography.fontWeight};
          padding-top: ${spacing};
          padding-bottom: ${spacing};
        }

        &-content {
          padding-top: ${spacing};
        }

        &-chevron {
          width: ${chevronWidth};
          min-width: ${chevronWidth};
          margin-inline-end: ${spacing};
          margin-inline-start: ${spacing};
        }
      }
    `;
  }}
`;

const StyledTooltip = styled(Tooltip)`
  display: inline-block;
`;

const InfoIcon = styled(Icon)`
  ${({ theme }) => css`
    display: inline-flex;
    margin-left: ${theme.spacing(1)};
  `}
`;
